import { Injectable, inject, computed } from '@angular/core';
import { AuthService } from './auth.service';

/**
 * Servicio de comprobación de permisos. Es la API única para verificar
 * permisos en el código de la aplicación.
 *
 * Implementación provisional (Fase 0.3): el array de permisos del
 * usuario se rellena en `mapUserInfoDto` (user.model.ts) a partir del
 * campo `accessLevel` del backend. Usuarios internos ('erp') reciben
 * todas las keys del catálogo; los demás reciben solo su accessLevel.
 * La comprobación de cualquier permiso del catálogo devuelve true para
 * usuarios internos sin necesidad de cambiar este servicio.
 *
 * Cuando el backend emita scopes en el JWT, solo hay que cambiar
 * `mapUserInfoDto` para que rellene `permissions` con los scopes
 * reales. El resto del código (consumidores de este servicio) no se
 * entera.
 *
 * El catálogo de permisos válidos vive en
 * `src/app/core/auth/permissions.ts` (PERMISSIONS).
 */
@Injectable({ providedIn: 'root' })
export class PermissionService {
  private readonly auth = inject(AuthService);

  private readonly permissions = computed(() => new Set(this.auth.currentUser()?.permissions ?? []));

  private readonly roles = computed(() => new Set(this.auth.currentUser()?.roles ?? []));

  hasPermission(permission: string): boolean {
    return this.permissions().has(permission);
  }

  /**
   * Roles de la sesión (`GET api/auth/mis-permisos`). Son una conveniencia (p. ej. saber si alguien es
   * `RESPONSABLE`): lo que se puede ver o hacer se decide SIEMPRE por permiso, nunca solo por rol.
   */
  hasRole(role: string): boolean {
    return this.roles().has(role);
  }

  hasAnyRole(roles: readonly string[]): boolean {
    return roles.some((r) => this.roles().has(r));
  }

  hasAnyPermission(permissions: string[]): boolean {
    return permissions.some((p) => this.permissions().has(p));
  }

  hasAllPermissions(permissions: string[]): boolean {
    return permissions.every((p) => this.permissions().has(p));
  }
}
